import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FileText, Plus, TerminalSquare, Upload } from 'lucide-react'
import type { ProjectList, Scope, ScopeActuals, ScopeCreate, ScopeForm, ScopeList, ScopeSetupStatus, ScopeStatus, ScopeVersions } from '@one-deployment/api'
import { Badge, Button, Card, Dialog, Input, Label, PageHeader, Segmented, Select, SkeletonRows, Textarea } from '@/components/ui'
import { Button as SButton } from '@/components/shadcn/button'
import { Age, EmptyState, ErrorState } from '@/components/status'
import { CreateProjectButton } from '@/features/projects/CreateProject'
import { SessionMain } from '@/features/sessions/Sessions'
import { useProjectSessions } from '@/features/sessions/data'
import { ApiError, enc, get, patch, post } from '@/lib/api'

const GENERAL = 'general'
const MAX_FILE = 200_000
const TONE: Record<ScopeStatus, 'muted' | 'info' | 'ok' | 'warn'> = { draft: 'muted', sent: 'info', won: 'ok', lost: 'warn' }
const errText = (e: unknown) => (e instanceof ApiError ? (e.what ?? e.message) : String(e))
const money = (n: number | null) => (n === null ? '—' : `£${n.toLocaleString('en-GB')}`)

/** Scoping (features/03). Admin only: the route is gated and the API answers 404 to anyone else. */
export function ScopesPage() {
  const setup = useQuery({ queryKey: ['scopes', 'setup'], queryFn: ({ signal }) => get<ScopeSetupStatus>('/api/scopes/setup', signal), refetchInterval: (q) => (q.state.data?.container?.state === 'ready' && q.state.data.sessionId ? 30_000 : 5000) })

  // Just the live Claude session: the scoping repo's /scope skill does the rest.
  return (
    <div className="flex h-full flex-col">
      {setup.error ? <ErrorState error={setup.error} retry={() => void setup.refetch()} /> : !setup.data ? <SkeletonRows rows={4} /> : !setup.data.ready ? <SetupCard repo={setup.data.repo} viaApp={setup.data.appCanReach} /> : (
        <div className="min-h-0 flex-1 overflow-hidden">
          <ScopingTerminal sessionId={setup.data.sessionId} containerState={setup.data.container?.state ?? null} />
        </div>
      )}
    </div>
  )
}

/**
 * The always-on Claude session in `general`, live as soon as Scopes opens.
 * The worker keeps it running (a minute cron restarts it), so an empty state
 * here only lasts while the container or session is coming up.
 */
function ScopingTerminal({ sessionId, containerState }: { sessionId: string | null; containerState: string | null }) {
  const sessions = useProjectSessions(GENERAL)
  const session = sessions.data?.sessions.find((x) => x.id === sessionId)
  if (!sessionId || !session) {
    return <EmptyState title={containerState === 'ready' ? 'Starting the scoping session…' : 'Starting the general container…'} body="It stays running from now on; this takes a minute the first time." />
  }
  // The same windows as any project session: scoping is the general project's session.
  return <div className="flex h-full min-h-0"><SessionMain session={session} project={GENERAL} /></div>
}

function SetupCard({ repo, viaApp }: { repo: string; viaApp: boolean }) {
  const qc = useQueryClient()
  const [token, setToken] = useState('')
  const [user, setUser] = useState('')
  const go = useMutation({
    mutationFn: () => post<ScopeSetupStatus>('/api/scopes/setup', viaApp ? {} : { token, ...(user.trim() ? { user: user.trim() } : {}) }),
    onSuccess: () => { setToken(''); void qc.invalidateQueries({ queryKey: ['scopes'] }) },
  })
  return (
    <div className="p-5">
      <Card title="Set up scoping">
        <div className="flex max-w-xl flex-col gap-3 p-3 text-sm">
          <p className="text-fg-subtle">Scoping runs in a small container called <b>general</b> (1 GB, 1 CPU) with the private repo <b>{repo}</b> cloned in. Each scope is committed back, so every version is kept.</p>
          {viaApp ? (
            <p className="text-fg-subtle">The GitHub App can reach that repo, so no token is needed.</p>
          ) : (
            <>
              <p className="text-fg-subtle">Easiest: <Link to="/settings/connections" className="text-accent hover:underline">create the GitHub App on Connections</Link> and grant it this repo; then come back and press Set up. Or give it a GitHub token that can read and push to that repo only (a fine-grained token with Contents: read and write). It is stored encrypted and never shown again.</p>
              <div><Label htmlFor="scope-token">GitHub token</Label><Input id="scope-token" type="password" autoComplete="off" value={token} onChange={(e) => setToken(e.target.value)} /></div>
              <div><Label htmlFor="scope-user">GitHub user it belongs to (optional)</Label><Input id="scope-user" value={user} onChange={(e) => setUser(e.target.value)} /></div>
            </>
          )}
          {go.error && <div role="alert" className="text-xs text-err">{errText(go.error)}</div>}
          <div><Button variant="primary" disabled={go.isPending || (!viaApp && token.trim().length < 20)} onClick={() => go.mutate()}>{go.isPending ? 'Setting up…' : 'Set up'}</Button></div>
        </div>
      </Card>
    </div>
  )
}

function NewScope({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [form, setForm] = useState({ client: '', title: '', brief: '' })
  const [files, setFiles] = useState<ScopeCreate['files']>([])
  const [error, setError] = useState<string | null>(null)
  const input = useRef<HTMLInputElement>(null)
  const create = useMutation({
    mutationFn: () => post<Scope>('/api/scopes', { ...form, files }),
    onSuccess: (s) => {
      setForm({ client: '', title: '', brief: '' }); setFiles([]); setError(null); onOpenChange(false)
      void qc.invalidateQueries({ queryKey: ['scopes'] })
      void navigate({ to: '/scopes/$scope', params: { scope: s.id } })
    },
    onError: (e) => setError(errText(e)),
  })
  // Claude reads the files as part of the brief, so only text goes in.
  const add = async (list: FileList | null) => {
    for (const f of [...(list ?? [])]) {
      if (f.size > MAX_FILE) { setError(`${f.name} is over 200 KB`); continue }
      const text = await f.text()
      if (text.includes('\u0000')) { setError(`${f.name} is not a text file; paste what matters from it into the brief`); continue }
      setFiles((cur) => [...cur, { name: f.name, text }].slice(0, 10))
    }
  }
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value })
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="New scope" description="Opens a Claude session in general that runs /scope on this brief." width={620}>
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-3">
          <div><Label htmlFor="sc-client">Client</Label><Input id="sc-client" value={form.client} onChange={set('client')} /></div>
          <div><Label htmlFor="sc-title">Name</Label><Input id="sc-title" value={form.title} onChange={set('title')} /></div>
        </div>
        <div><Label htmlFor="sc-brief">Brief</Label><Textarea id="sc-brief" rows={10} value={form.brief} onChange={set('brief')} placeholder="What the client wants, who uses it, deadlines, budget hints" /></div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <input ref={input} type="file" multiple accept=".txt,.md,.csv,.json,.html,text/*" className="hidden" onChange={(e) => { void add(e.target.files); e.target.value = '' }} />
          <Button size="sm" onClick={() => input.current?.click()}><Upload size={13} /> Add text files</Button>
          {files.map((f, i) => (
            <span key={`${f.name}-${i}`} className="flex items-center gap-1 rounded border border-border px-2 py-0.5"><FileText size={12} />{f.name}
              <SButton variant="ghost" size="icon-xs" aria-label={`Remove ${f.name}`} className="size-4 rounded-xs text-fg-faint hover:bg-transparent hover:text-fg" onClick={() => setFiles(files.filter((_, j) => j !== i))}>×</SButton>
            </span>
          ))}
        </div>
        {error && <div role="alert" className="text-xs text-err">{error}</div>}
        <div className="flex justify-end gap-2">
          <Button onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="primary" disabled={create.isPending || !form.client.trim() || !form.title.trim() || !form.brief.trim()} onClick={() => create.mutate()}>{create.isPending ? 'Starting…' : 'Start scoping'}</Button>
        </div>
      </div>
    </Dialog>
  )
}

function ScopeDetail({ id }: { id: string }) {
  const qc = useQueryClient()
  const scope = useQuery({
    queryKey: ['scopes', id],
    queryFn: ({ signal }) => get<Scope>(`/api/scopes/${enc(id)}`, signal),
    // A session started a moment ago shows as live once the box reports it.
    refetchInterval: (q) => (q.state.data?.sessions.some((s) => !s.live) ? 4000 : 30_000),
  })
  const versions = useQuery({ queryKey: ['scopes', id, 'versions'], queryFn: ({ signal }) => get<ScopeVersions>(`/api/scopes/${enc(id)}/versions`, signal), refetchInterval: 30_000 })
  const [sha, setSha] = useState<string>('')
  const [copy, setCopy] = useState<'internal' | 'client'>('internal')
  useEffect(() => { setSha(''); setCopy('internal') }, [id])
  const form = useQuery({
    queryKey: ['scopes', id, 'form', sha, copy],
    queryFn: ({ signal }) => get<ScopeForm>(`/api/scopes/${enc(id)}/form?${new URLSearchParams({ ...(sha ? { sha } : {}), ...(copy === 'client' ? { client: '1' } : {}) })}`, signal),
  })
  const projects = useQuery({ queryKey: ['projects'], queryFn: ({ signal }) => get<ProjectList>('/api/projects', signal) })
  const update = useMutation({
    mutationFn: (body: Partial<{ status: ScopeStatus; priceGbp: number | null; projectKey: string | null }>) => patch<Scope>(`/api/scopes/${enc(id)}`, body, scope.data!.version),
    onSuccess: (s) => { qc.setQueryData(['scopes', id], s); void qc.invalidateQueries({ queryKey: ['scopes'], exact: true }) },
  })
  const session = useMutation({
    mutationFn: () => post<Scope>(`/api/scopes/${enc(id)}/sessions`),
    onSuccess: (s) => qc.setQueryData(['scopes', id], s),
  })
  const [price, setPrice] = useState('')
  useEffect(() => { setPrice(scope.data?.priceGbp?.toString() ?? '') }, [scope.data?.priceGbp, id])

  if (scope.error) return <ErrorState error={scope.error} retry={() => void scope.refetch()} />
  if (!scope.data) return <SkeletonRows rows={6} />
  const s = scope.data
  const savePrice = () => {
    const n = price.trim() === '' ? null : Number(price.replace(/[£,\s]/g, ''))
    if (n !== null && !Number.isInteger(n)) return
    if (n !== s.priceGbp) update.mutate({ priceGbp: n })
  }

  return (
    <div className="flex flex-col gap-4 p-5">
      <div>
        <div className="text-lg font-semibold">{s.client} · {s.title}</div>
        <div className="text-xs text-fg-subtle">{s.path} · started by {s.createdBy ?? 'someone'} <Age from={s.createdAt} /></div>
      </div>
      <div className="flex flex-wrap items-end gap-4 text-sm">
        <div><Label>Status</Label><Segmented value={s.status} onChange={(v) => update.mutate({ status: v as ScopeStatus })} options={[{ value: 'draft', label: 'Draft' }, { value: 'sent', label: 'Sent' }, { value: 'won', label: 'Won' }, { value: 'lost', label: 'Lost' }]} /></div>
        <div><Label htmlFor="sc-price">Price (£)</Label><Input id="sc-price" className="h-7 w-28 text-xs" inputMode="numeric" value={price} onChange={(e) => setPrice(e.target.value)} onBlur={savePrice} onKeyDown={(e) => { if (e.key === 'Enter') savePrice() }} /></div>
        {update.error && <span role="alert" className="text-xs text-err">{errText(update.error)}</span>}
      </div>
      {s.status === 'won' && (
        <Card title="Project">
          <div className="flex flex-wrap items-center gap-2 p-3 text-xs">
            {s.projectKey ? (
              <><span>Became</span><Link to="/p/$project" params={{ project: s.projectKey }} className="text-accent hover:underline">{s.projectKey}</Link><Button size="sm" variant="ghost" onClick={() => update.mutate({ projectKey: null })}>Unlink</Button></>
            ) : (
              <>
                <span className="text-fg-subtle">Won scopes become a project, which starts in Foundations.</span>
                <CreateProjectButton trigger={<Button size="sm" variant="primary">Create the project</Button>} />
                <Select aria-label="Link to an existing project" value="" onChange={(e) => e.target.value && update.mutate({ projectKey: e.target.value })}>
                  <option value="">Link to a project…</option>
                  {projects.data?.projects.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
                </Select>
              </>
            )}
          </div>
        </Card>
      )}
      {s.status === 'won' && s.projectKey && <Actuals id={s.id} />}
      <Card title="Sessions" actions={<Button size="sm" disabled={session.isPending} onClick={() => session.mutate()}><Plus size={12} /> New session</Button>}>
        <ul className="text-sm">
          {s.sessions.map((x) => (
            <li key={x.tmuxSession} className="flex items-center gap-2 border-b border-border px-3 py-1.5 last:border-0">
              <TerminalSquare size={13} className="text-fg-subtle" />
              {x.id && x.live ? <Link to="/p/$project/s/$session" params={{ project: GENERAL, session: x.id }} className="flex-1 text-accent hover:underline">{x.label ?? x.tmuxSession}</Link> : <span className="flex-1 text-fg-subtle">{x.label ?? x.tmuxSession}</span>}
              <span className="text-xs text-fg-faint">{x.live ? 'open' : x.id ? 'closed' : 'starting…'}</span>
            </li>
          ))}
        </ul>
        {session.error && <div role="alert" className="px-3 pb-2 text-xs text-err">{errText(session.error)}</div>}
      </Card>
      <Card
        title="Scope form"
        actions={<div className="flex items-center gap-2">
          <Select aria-label="Version" value={sha} onChange={(e) => setSha(e.target.value)}>
            <option value="">Latest</option>
            {versions.data?.versions.map((v) => <option key={v.sha} value={v.sha}>{new Date(v.at).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })} · {v.message.slice(0, 50)}</option>)}
          </Select>
          <Segmented value={copy} onChange={(v) => setCopy(v as 'internal' | 'client')} options={[{ value: 'internal', label: 'Internal' }, { value: 'client', label: 'Client copy' }]} />
        </div>}
      >
        {form.error ? <ErrorState error={form.error} retry={() => void form.refetch()} /> : !form.data ? <SkeletonRows rows={8} /> : form.data.text === null ? (
          <EmptyState title="Not written yet" body={`Claude writes ${s.path} and pushes it; it shows here once it is in the repo.`} />
        ) : (
          <pre className="max-h-[70vh] overflow-auto whitespace-pre p-3 font-mono text-[11px] leading-snug">{form.data.text}</pre>
        )}
        {copy === 'client' && form.data?.text && <div className="border-t border-border px-3 py-1.5 text-xs text-fg-subtle">Section 9 (internal pricing) is left out.</div>}
      </Card>
      <Card title="Brief"><div className="whitespace-pre-wrap p-3 text-sm text-fg-subtle">{s.brief}</div></Card>
    </div>
  )
}

/** Real hours of the delivered project, written to the scoping repo for the next quote (features/03). */
function Actuals({ id }: { id: string }) {
  const qc = useQueryClient()
  const key = ['scope-actuals', id] as const
  const q = useQuery({ queryKey: key, queryFn: ({ signal }) => get<ScopeActuals>(`/api/scopes/${enc(id)}/actuals`, signal) })
  const [hours, setHours] = useState('')
  const [notes, setNotes] = useState('')
  useEffect(() => {
    if (q.data?.saved) { setHours(String(q.data.saved.hours)); setNotes(q.data.saved.notes) }
  }, [q.data?.saved])
  const save = useMutation({ mutationFn: () => post<ScopeActuals>(`/api/scopes/${enc(id)}/actuals`, { hours: Number(hours), notes }), onSuccess: (v) => qc.setQueryData(key, v) })
  const f = q.data?.facts
  if (!f) return null
  return (
    <Card title="Real hours">
      <div className="flex flex-col gap-2 p-3 text-xs">
        <p className="text-fg-subtle">
          Quoted {f.quotedGbp ? `£${f.quotedGbp}` : 'not recorded'} · {f.weeks ?? '?'} weeks to production · {f.ticketsDone} tickets done · Claude: {f.robotHours} h robot runs, {f.sessionHours} h in sessions
        </p>
        {!f.delivered ? <p>Record real hours once {f.projectKey} is in Production or Support.</p> : (
          <>
            <p className="text-fg-subtle">The hours people spent. Saved to the scoping repo, where the next scope calibrates against it.</p>
            <div className="flex items-end gap-2">
              <div><Label htmlFor="act-h">Real hours</Label><Input id="act-h" className="h-7 w-24 text-xs" inputMode="decimal" value={hours} onChange={(e) => setHours(e.target.value)} /></div>
              {hours && Number(hours) > 0 && f.quotedGbp && <span className="pb-1.5 text-fg-subtle">£{Math.round(f.quotedGbp / Number(hours))}/hour real</span>}
            </div>
            <div><Label htmlFor="act-n">What made it longer or shorter</Label><Textarea id="act-n" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
            <div className="flex items-center gap-2">
              <Button variant="primary" disabled={save.isPending || !(Number(hours) > 0)} onClick={() => save.mutate()}>{q.data?.saved ? 'Update' : 'Save to calibration'}</Button>
              {q.data?.saved && <span className="text-fg-faint">Saved by {q.data.saved.recordedBy} · {q.data.saved.path}</span>}
            </div>
            {save.error && <div role="alert" className="text-err">{errText(save.error)}</div>}
          </>
        )}
      </div>
    </Card>
  )
}
